import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Statistic, Spin, Space, DatePicker, Button, Alert, Table, Segmented } from 'antd';
import {
  DollarOutlined, ProjectOutlined, BankOutlined, DownloadOutlined, CalendarOutlined,
} from '@ant-design/icons';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import dayjs from 'dayjs';
import api from '../libs/api';

// Both chart cards reserve the same space for their caption. Defined once so
// the two cannot drift apart the next time one of them gains a line.
const CHART_NOTE_H = 46;

const fmt$ = (v) => {
  if (v == null) return '-';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Number(v).toFixed(0)}`;
};

export default function BorrowingBaseTab() {
  // Default to the month containing yesterday. Yesterday is a natural "as-of" date
  // for a monthly report (today's numbers not yet finalised).
  const [month, setMonth] = useState(dayjs().subtract(1, 'day').startOf('month'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!month) return;
    setLoading(true);
    setError(null);
    // Borrowing-base needs longer than the default 30 s — the billing-table
    // scan for issued-invoice counts is ~10-15 s and Cloud Run cold-start
    // adds another 5-15 s on the first request after idle.
    api.get('/api/ppa/borrowing-base', { params: { month: month.format('YYYY-MM') }, timeout: 90_000 })
      .then(r => setData(r.data.data))
      .catch(e => setError(e.response?.data?.message || e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [month]);

  const handleDownload = () => {
    if (!month) return;
    setDownloading(true);
    api.get('/api/ppa/borrowing-base/download', {
      params: { month: month.format('YYYY-MM') },
      responseType: 'blob',
      timeout: 180_000,
    })
      .then(r => {
        const url = URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `GTL Borrowing Base Calculation - Monthly ${month.format('YYYY-MM')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(e => setError(e.message || 'Download failed'))
      .finally(() => setDownloading(false));
  };

  const stats = data?.stats || {};
  const history = data?.history || [];
  const deltas = data?.deltas || [];
  /**
   * Which measure the additions chart is drawn in.
   *
   * 'monthly' is the revenue moving in and out each month; 'bb' is what those
   * same systems are worth to the borrowing base. Two different questions about
   * one set of movements, and they differ by two orders of magnitude, so they
   * cannot share an axis — hence a switch rather than a second series.
   */
  const [deltaMeasure, setDeltaMeasure] = useState('monthly');
  const bySystems = deltaMeasure === 'systems';
  const byBb = deltaMeasure === 'bb';
  const k = (base) => {
    if (bySystems) return `${base}Count`;
    return byBb ? `${base}Bb` : base;
  };
  // Systems are a count, not money. Outflows are stored negative so the stack
  // reads the same way in every measure, and the sign is dropped for display —
  // the axis already says which side of zero a bar is on.
  const fmtDelta = (v) => (bySystems ? Math.abs(Number(v) || 0).toLocaleString() : fmt$(v));
  const capexTable = data?.capexTable || [];

  const capexColumns = useMemo(() => [
    { title: 'Project ID', dataIndex: 'projectId', key: 'projectId', width: 170, fixed: 'left' },
    { title: 'Commission Date', dataIndex: 'commissionDate', key: 'commissionDate', width: 130 },
    { title: 'Issued Invoices', dataIndex: 'issuedInvoices', key: 'issuedInvoices', align: 'right', width: 120, sorter: (a, b) => a.issuedInvoices - b.issuedInvoices },
    { title: 'Total Invoices',  dataIndex: 'totalInvoices',  key: 'totalInvoices',  align: 'right', width: 110 },
    { title: 'Book Value',      dataIndex: 'bookValue',      key: 'bookValue',      align: 'right', width: 120, render: fmt$, sorter: (a, b) => a.bookValue - b.bookValue },
    { title: 'Payments Remaining', dataIndex: 'paymentsRemaining', key: 'paymentsRemaining', align: 'right', width: 130 },
    { title: 'Final Month',     dataIndex: 'finalMonth',     key: 'finalMonth',     width: 120 },
    {
      title: 'Monthly PPA revenue', dataIndex: 'monthlyPayment', key: 'monthlyPayment', align: 'right', width: 150,
      render: (v) => v == null ? '-' : `$${v.toFixed(2)}`,
      sorter: (a, b) => a.monthlyPayment - b.monthlyPayment,
    },
  ], []);

  return (
    <>
      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space wrap size="middle" align="center">
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          <DatePicker.MonthPicker
            value={month}
            onChange={(m) => setMonth(m)}
            format="MMM YYYY"
            size="small"
            allowClear={false}
          />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloading}
            disabled={!data}
            onClick={handleDownload}
          >
            Download Excel report
          </Button>
          <span style={{ fontSize: 11, color: '#999' }}>
            Advance rate {data ? (data.advanceRate * 100).toFixed(2) + '%' : '—'} · discount rate {data ? (data.discountRate * 100).toFixed(0) + '%' : '—'}
          </span>
        </Space>
      </Card>

      {error && <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} onClose={() => setError(null)} />}

      <Spin spinning={loading}>
        {/* Row 2: KPI cards */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Card style={{ minHeight: 110 }}>
              <Statistic
                title="Projects (eligible)"
                value={stats.totalProjects || 0}
                prefix={<ProjectOutlined style={{ color: '#54A2CC' }} />}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Term {stats.termProjects || 0} · Capex-only {stats.capexOnlyProjects || 0}
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card style={{ minHeight: 110 }}>
              <Statistic
                title="Borrowing Base — Term"
                value={stats.borrowingBaseTerm || 0}
                formatter={(v) => fmt$(v)}
                prefix={<BankOutlined style={{ color: '#845EC2' }} />}
                valueStyle={{ color: '#845EC2' }}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Fixed batch (pre-facility inception)
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card style={{ minHeight: 110 }}>
              <Statistic
                title="Borrowing Base — Capex"
                value={stats.borrowingBaseCapex || 0}
                formatter={(v) => fmt$(v)}
                prefix={<DollarOutlined style={{ color: '#05A677' }} />}
                valueStyle={{ color: '#05A677' }}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Target Debt (Capex) = {fmt$(stats.targetDebtBalanceCapex || 0)}
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card style={{ minHeight: 110 }}>
              <Statistic
                title="PPA Capex Limit (this month)"
                value={stats.ppaCapexLimit || 0}
                formatter={(v) => fmt$(v)}
                prefix={<DollarOutlined style={{ color: '#f5b759' }} />}
                valueStyle={{ color: '#f5b759' }}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Total Net Investment × 90%
              </div>
            </Card>
          </Col>
        </Row>

        {/* Row 3: two charts */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12} style={{ display: 'flex' }}>
            {/* Both cards stretch to the row and reserve the same footer, so the
                caption under the right-hand chart cannot make it the taller of
                the two. */}
            <Card
              title="Borrowing Base — trailing 3 months (precise)"
              style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
              styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column' } }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={history} margin={{ top: 8, right: 20, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" fontSize={11} />
                  <YAxis tickFormatter={(v) => fmt$(v)} fontSize={11} />
                  <Tooltip formatter={(v) => fmt$(v)} />
                  <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: 8 }} />
                  <Bar dataKey="bbCapex" name="Capex (all)" fill="#05A677" isAnimationActive={false} />
                  <Bar dataKey="bbTerm"  name="Term"        fill="#845EC2" isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
              <div style={{ fontSize: 11, color: '#999', marginTop: 4, minHeight: CHART_NOTE_H }}>
                The borrowing base as it stood at each month end, recomputed at that cutoff
                rather than carried back from today.
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={12} style={{ display: 'flex' }}>
            <Card
              title="Capex additions / outflows"
              style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
              styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column' } }}
              extra={(
                <Segmented
                  size="small"
                  value={deltaMeasure}
                  onChange={setDeltaMeasure}
                  options={[
                    { label: 'Systems', value: 'systems' },
                    { label: 'Monthly $', value: 'monthly' },
                    { label: 'Borrowing base', value: 'bb' },
                  ]}
                />
              )}
            >
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={deltas} margin={{ top: 8, right: 20, bottom: 8, left: 8 }} stackOffset="sign">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" fontSize={11} />
                  <YAxis tickFormatter={fmtDelta} fontSize={11} />
                  <Tooltip formatter={fmtDelta} />
                  <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: 8 }} />
                  <Bar dataKey={k('added')}      name="New commissioned" stackId="a" fill="#05A677" isAnimationActive={false} />
                  <Bar dataKey={k('runoff')}     name="Natural runoff"   stackId="a" fill="#bdbdbd" isAnimationActive={false} />
                  <Bar dataKey={k('buyout')}     name="Buyout"           stackId="a" fill="#f5b759" isAnimationActive={false} />
                  <Bar dataKey={k('arrearsOut')} name="Fell into arrears" stackId="a" fill="#FA5252" isAnimationActive={false} />
                  {/* A stock, not a movement, so it is a line and not another
                      bar in the stack: the bars sum to the month's change and
                      this one does not belong in that sum. */}
                  <Line
                    type="monotone"
                    dataKey={bySystems ? 'nonEligibleCount' : (byBb ? 'nonEligibleBb' : 'nonEligibleMonthly')}
                    name="Non-eligible at month end"
                    stroke="#7c4dff" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
              <div style={{ fontSize: 11, color: '#999', marginTop: 4, minHeight: CHART_NOTE_H }}>
                Bars are the month's movement — additions positive, runoff / buyout / arrears
                negative. The line is a position, not a movement: what sits outside the base at
                that month end, counted fresh at each cutoff.
                {bySystems && ' Counted as systems.'}
                {deltaMeasure === 'monthly' && ' Measured as monthly revenue.'}
                {byBb && (
                  " Valued as borrowing base: each group's own discounted cash flows, without the"
                  + ' portfolio service cost, which is not a charge a single system carries.'
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Big table: Borrowing Base Capex projects */}
        <Card
          style={{ marginTop: 16 }}
          title={`Borrowing Base Capex — ${capexTable.length} eligible projects (as of ${data?.asOf || '—'})`}
          extra={
            <span style={{ fontSize: 12, color: '#999' }}>
              Capex facility only. Excludes buyouts, defaults, and non-eligible.
            </span>
          }
        >
          <Table
            dataSource={capexTable}
            columns={capexColumns}
            rowKey="projectId"
            size="small"
            scroll={{ x: 1000, y: 500 }}
            pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100', '200'] }}
          />
        </Card>
      </Spin>
    </>
  );
}
